import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { useAuth } from "../auth/AuthContext";

// Meniul CRM: se ascund modulele nepermise (regula din plan); backend-ul
// verifică oricum fiecare endpoint.
const MENU = [
  {
    titlu: "Operațional",
    items: [
      { label: "Dashboard", icon: "solar:home-smile-angle-outline", to: "/dashboard", perm: "dashboard.read" },
      { label: "Leaduri", icon: "solar:users-group-rounded-outline", to: "/leaduri", perm: "lead.read" },
      { label: "Calendar activități", icon: "solar:calendar-outline", to: "/activitati", perm: "lead.read" },
      { label: "Clienți", icon: "solar:hand-shake-outline", to: "/clienti", perm: "client.read" },
      { label: "Proiecte tehnice", icon: "solar:code-square-outline", to: "/proiecte", perms: ["project.read", "project.read.assigned"] },
      { label: "Proiecte în lucru", icon: "solar:clipboard-list-outline", to: "/proiecte-in-lucru", perms: ["project.read", "project.read.assigned"] },
      { label: "Raportare săptămânală", icon: "solar:document-text-outline", to: "/raportare", perms: ["report.read", "report.own"] },
    ],
  },
  {
    titlu: "SEO",
    items: [
      {
        label: "SEO",
        icon: "solar:magnifer-outline",
        perms: ["seo.read", "seo.read.assigned"],
        copii: [
          { label: "Proiecte SEO", to: "/seo/proiecte" },
          { label: "Calendar SEO", to: "/seo/calendar" },
          { label: "Rapoarte azi", to: "/seo/rapoarte-azi" },
        ],
      },
    ],
  },
  {
    titlu: "Administrare",
    items: [
      {
        label: "Finanțe",
        icon: "solar:wallet-outline",
        perm: "finance.read",
        copii: [
          { label: "Situația financiară", to: "/finante/situatie" },
          { label: "Încasări", to: "/finante/incasari" },
          { label: "Cheltuieli", to: "/finante/cheltuieli" },
          { label: "Management financiar", to: "/finante/management" },
          { label: "Conturi", to: "/finante/conturi" },
          { label: "Analiză", to: "/finante/analiza" },
        ],
      },
      { label: "Angajați și roluri", icon: "solar:users-group-two-rounded-outline", to: "/angajati", perm: "users.read" },
      { label: "Setări", icon: "solar:settings-outline", to: "/setari", perm: "settings.read" },
    ],
  },
];

function MenuItem({ item }) {
  const { hasPerm, hasAnyPerm } = useAuth();
  const location = window.location.pathname;
  const [deschis, setDeschis] = useState(
    item.copii?.some((c) => location.startsWith(c.to)) ?? false
  );

  if (item.perm && !hasPerm(item.perm)) return null;
  if (item.perms && !hasAnyPerm(item.perms)) return null;
  if (item.perm && !item.perms && !hasPerm(item.perm)) return null;

  const activ = item.copii ? item.copii.some((c) => location.startsWith(c.to)) : location === item.to;

  if (!item.copii) {
    return (
      <li>
        <NavLink to={item.to} className={({ isActive }) => (isActive ? "active-page" : "")}>
          <Icon icon={item.icon} className="menu-icon" />
          <span>{item.label}</span>
        </NavLink>
      </li>
    );
  }

  return (
    <li className={`dropdown ${deschis || activ ? "open" : ""}`}>
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          setDeschis(!deschis);
        }}
      >
        <Icon icon={item.icon} className="menu-icon" />
        <span>{item.label}</span>
      </Link>
      <ul className="sidebar-submenu">
        {item.copii.map((c) => (
          <li key={c.to}>
            <NavLink to={c.to} className={({ isActive }) => (isActive ? "active-page" : "")}>
              <i className="ri-circle-fill circle-icon text-primary-600 w-auto" /> {c.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function CrmLayout({ children }) {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const { user, logout, hasPerm, hasAnyPerm } = useAuth();
  const navigate = useNavigate();

  // Un item e vizibil doar dacă rolul are permisiunea cerută de el.
  const vizibil = (item) => {
    if (item.perm) return hasPerm(item.perm);
    if (item.perms) return hasAnyPerm(item.perms);
    return true;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <aside className={sidebarActive ? "sidebar active" : mobileMenu ? "sidebar sidebar-open" : "sidebar"}>
        <button onClick={() => setMobileMenu(false)} type="button" className="sidebar-close-btn">
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/dashboard" className="sidebar-logo">
            <img src="assets/images/logo.png" alt="iCode CRM" className="light-logo" />
            <img src="assets/images/logo-light.png" alt="iCode CRM" className="dark-logo" />
            <img src="assets/images/logo-icon.png" alt="iCode CRM" className="logo-icon" />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="crm-sidebar-menu">
            {MENU.map((grup) => {
              const vizibile = grup.items.filter(vizibil);
              if (vizibile.length === 0) return null;
              return (
                <React.Fragment key={grup.titlu}>
                  <li className="sidebar-menu-group-title">{grup.titlu}</li>
                  {vizibile.map((item) => (
                    <MenuItem key={item.label} item={item} />
                  ))}
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button type="button" className="sidebar-toggle" onClick={() => setSidebarActive(!sidebarActive)}>
                  <Icon
                    icon={sidebarActive ? "iconoir:arrow-right" : "heroicons:bars-3-solid"}
                    className="icon text-2xl non-active"
                  />
                </button>
                <button onClick={() => setMobileMenu(true)} type="button" className="sidebar-mobile-toggle">
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <ThemeToggleButton />
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <img
                      src="assets/images/logo-icon.png"
                      alt="iCode"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                    />
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-2">{user?.nume}</h6>
                        <span className="text-secondary-light fw-medium text-sm">{user?.role}</span>
                      </div>
                    </div>
                    <ul className="to-top-list">
                      {hasPerm("settings.read") && (
                        <li>
                          <Link
                            className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
                            to="/setari"
                          >
                            <Icon icon="icon-park-outline:setting-two" className="icon text-xl" /> Setări
                          </Link>
                        </li>
                      )}
                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                          onClick={handleLogout}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" /> Deconectare
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">{children}</div>

        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© 2026 iCode CRM. Toate drepturile rezervate.</p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
}
